import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigationClient } from '@/api-proxy/useProxiedClient'
import { getSystemStatusOptions } from '@/api/navigationApi'
import { extendKey } from '@/utils/queryKey'
import { isServiceUnavailable } from '@/api-proxy/serviceAvailability'
import { CAMERA_POSITIONS, FRONT_CAMERA_POSITIONS } from './helpers'

/**
 * Rear cameras are only fitted on some vehicles. /system-status is fetched once
 * per vehicle; until it succeeds the front cameras are shown. A failed fetch stays
 * retryable, including when the window regains focus.
 */
const useAvailableCameras = (source: string) => {
  const client = useNavigationClient(source)

  const {
    data: systemStatus,
    error,
    isError,
    refetch,
  } = useQuery({
    ...extendKey(getSystemStatusOptions({ client }), source),
    staleTime: Infinity,
    retry: 1,
  })

  const cameras = useMemo(() => {
    if (!systemStatus) return FRONT_CAMERA_POSITIONS
    return CAMERA_POSITIONS.filter(position => systemStatus.cameras[position])
  }, [systemStatus])

  return { cameras, isError, serviceUnavailable: isServiceUnavailable(error), refetch }
}

export default useAvailableCameras
